import type { WorkbookDefinition } from '@erp/shared-types';

/** What kind of item an edit touched, for naming it ("Added worksheet …"). */
export type WorkbookHistoryItem =
  'file' | 'source' | 'sheet' | 'output' | 'instruction';

/** A filter as the history describes it: a Worksheet's or an Entity Source's. */
export interface WorkbookHistoryFilter {
  column: string;
  operator: string;
  value: string;
  values?: readonly string[];
}

/**
 * What one edit did to a Definition, as the Workbook's edit history names it:
 * a verb and its object ("Added column Constant to Products 1"). `name` is the
 * item's name, null for an untitled Instruction; `sheet` names a Worksheet.
 */
export type WorkbookHistoryChange =
  | { kind: 'renamed-workbook' }
  | { kind: 'edited-instructions' }
  | { kind: 'reordered' }
  | { kind: 'edited-workbook' }
  | {
      kind: 'added' | 'removed' | 'moved' | 'edited';
      item: WorkbookHistoryItem;
      name: string | null;
    }
  | { kind: 'renamed'; item: WorkbookHistoryItem; from: string; to: string }
  | {
      kind:
        | 'column-added'
        | 'column-removed'
        | 'formula-changed'
        | 'column-type-changed'
        | 'column-format-changed'
        | 'column-edited'
        | 'lookup-added'
        | 'lookup-removed'
        | 'lookup-changed';
      sheet: string;
      column: string;
    }
  | { kind: 'column-renamed'; sheet: string; from: string; to: string }
  | {
      kind:
        | 'columns-reordered'
        | 'sort-cleared'
        | 'pivot-changed'
        | 'cells-edited'
        | 'table-changed';
      sheet: string;
    }
  | { kind: 'cell-edited' | 'cell-cleared'; sheet: string; cell: string }
  | {
      kind: 'sorted';
      sheet: string;
      column: string;
      direction: 'asc' | 'desc';
    }
  | { kind: 'filtered'; name: string; filter: WorkbookHistoryFilter }
  | { kind: 'filter-removed'; name: string; column: string };

export interface DescribedChange {
  change: WorkbookHistoryChange;
  /**
   * Set only for typing into one text field; consecutive edits of one group
   * merge into one step of the history (see `@/lib/undoTree`).
   */
  group?: string;
}

/**
 * Fields typed into character by character. Only an edit of one of these
 * groups, so a discrete change (a sort direction, a type, a drag) is always a
 * step of its own.
 */
const TEXT_FIELDS = new Set([
  'name',
  'title',
  'description',
  'instructions',
  'expression',
  'value',
  'fileName',
  'tabName',
  'outputColumn',
  'numberFormat',
]);

type Entry = { id: string } & Record<string, unknown>;
type Collection = 'files' | 'sources' | 'sheets' | 'outputs' | 'processSteps';
const COLLECTIONS: Readonly<Record<Collection, WorkbookHistoryItem>> = {
  files: 'file',
  sources: 'source',
  sheets: 'sheet',
  outputs: 'output',
  processSteps: 'instruction',
};

/**
 * Whether two JSON values have the same content. A part both share by
 * reference is the same without being read, so comparing a Definition with
 * its edit (which keeps every part the edit didn't touch) reads only what
 * changed; a missing and an undefined field are the same, as saved JSON is.
 */
export function sameContent(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!isObject(a) || !isObject(b)) return false;
  if (Array.isArray(a) || Array.isArray(b)) return sameArray(a, b);
  return sameRecord(a, b);
}

/** A JSON array or object; `sameContent` reads both by their entries. */
type Composite = Readonly<Record<string, unknown>> | readonly unknown[];

function isObject(value: unknown): value is Composite {
  return typeof value === 'object' && value !== null;
}

function sameArray(a: Composite, b: Composite): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
    return false;
  return a.every((item, index) => sameContent(item, b[index]));
}

function definedEntries(value: Composite): [string, unknown][] {
  return Object.entries(value).filter(([, entry]) => entry !== undefined);
}

function sameRecord(a: Composite, b: Composite): boolean {
  const left = definedEntries(a);
  const right: Record<string, unknown> = Object.fromEntries(definedEntries(b));
  if (left.length !== Object.keys(right).length) return false;
  return left.every(
    ([key, value]) => key in right && sameContent(value, right[key]),
  );
}

/** Whether two Definitions are the same, by content (see {@link sameContent}). */
export function sameDefinition(
  a: WorkbookDefinition,
  b: WorkbookDefinition,
): boolean {
  return sameContent(a, b);
}

function same(a: unknown, b: unknown): boolean {
  return sameContent(a, b);
}

function changedKeys(
  left: Readonly<Record<string, unknown>>,
  right: Readonly<Record<string, unknown>>,
): string[] {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].filter((key) => !same(left[key], right[key]));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function entryName(collection: Collection, entry: Entry): string | null {
  const name = collection === 'processSteps' ? entry.title : entry.name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/** The only key that differs between two records, if exactly one does. */
function onlyChangedKey(
  before: Readonly<Record<string, unknown>>,
  after: Readonly<Record<string, unknown>>,
): string | null {
  const [key, ...more] = changedKeys(before, after);
  return key !== undefined && !more.length ? key : null;
}

/** How one list of records became another, by one element. */
type ListChange =
  | { kind: 'added'; element: Record<string, unknown> }
  | { kind: 'removed'; element: Record<string, unknown> }
  | {
      kind: 'changed';
      index: number;
      before: Record<string, unknown>;
      element: Record<string, unknown>;
      field: string | null;
    }
  | { kind: 'reordered' }
  | { kind: 'other' };

function addedOrRemoved(
  longer: Record<string, unknown>[],
  shorter: Record<string, unknown>[],
): Record<string, unknown> | undefined {
  const extra = longer.filter(
    (element) => !shorter.some((other) => same(other, element)),
  );
  return extra.length === 1 ? extra[0] : undefined;
}

function listChange(before: unknown, after: unknown): ListChange {
  const old = records(before);
  const next = records(after);
  if (next.length === old.length + 1) return oneElement('added', next, old);
  if (next.length + 1 === old.length) return oneElement('removed', old, next);
  if (next.length !== old.length) return { kind: 'other' };
  return sameLengthChange(old, next);
}

function oneElement(
  kind: 'added' | 'removed',
  longer: Record<string, unknown>[],
  shorter: Record<string, unknown>[],
): ListChange {
  const element = addedOrRemoved(longer, shorter);
  return element ? { kind, element } : { kind: 'other' };
}

/** One element changed in place, or several moved. */
function sameLengthChange(
  old: Record<string, unknown>[],
  next: Record<string, unknown>[],
): ListChange {
  const indexes = next.flatMap((element, index) =>
    same(element, old[index]) ? [] : [index],
  );
  if (!indexes.length) return { kind: 'other' };
  const [index] = indexes;
  const element = index === undefined ? undefined : next[index];
  const previous = index === undefined ? undefined : old[index];
  if (indexes.length > 1 || index === undefined || !element || !previous)
    return { kind: 'reordered' };
  return {
    kind: 'changed',
    index,
    before: previous,
    element,
    field: onlyChangedKey(previous, element),
  };
}

/** A column or Lookup added or removed, named by its `columnKey`. */
function addedOrRemovedColumn(
  list: Extract<ListChange, { kind: 'added' | 'removed' }>,
  sheet: string,
  kinds: {
    added: 'column-added' | 'lookup-added';
    removed: 'column-removed' | 'lookup-removed';
  },
  columnKey: 'name' | 'outputColumn',
): DescribedChange {
  return {
    change: {
      kind: kinds[list.kind],
      sheet,
      column: text(list.element[columnKey]),
    },
  };
}

function asFilter(element: Record<string, unknown>): WorkbookHistoryFilter {
  const values = Array.isArray(element.values)
    ? element.values.filter((value) => typeof value === 'string')
    : undefined;
  return {
    column: text(element.column),
    operator: text(element.operator),
    value: text(element.value),
    ...(values && { values }),
  };
}

function textGroup(
  id: string,
  path: string,
  field: string | null,
): { group?: string } {
  return field !== null && TEXT_FIELDS.has(field)
    ? { group: `${id}:${path}:${field}` }
    : {};
}

/** A Worksheet's or an Entity Source's filters, changed by one filter. */
function describeFilters(
  id: string,
  name: string,
  before: unknown,
  after: unknown,
): DescribedChange | null {
  const list = listChange(before, after);
  if (list.kind === 'added')
    return {
      change: { kind: 'filtered', name, filter: asFilter(list.element) },
    };
  if (list.kind === 'removed')
    return {
      change: {
        kind: 'filter-removed',
        name,
        column: text(list.element.column),
      },
    };
  if (list.kind !== 'changed') return null;
  return {
    change: { kind: 'filtered', name, filter: asFilter(list.element) },
    ...textGroup(id, `filters:${list.index}`, list.field),
  };
}

const COLUMN_FIELD_CHANGES = {
  expression: 'formula-changed',
  type: 'column-type-changed',
  numberFormat: 'column-format-changed',
} as const;

function isColumnField(
  field: string | null,
): field is keyof typeof COLUMN_FIELD_CHANGES {
  return field !== null && field in COLUMN_FIELD_CHANGES;
}

/** A columnar Worksheet's columns, changed by one column or reordered. */
function describeColumns(
  id: string,
  sheet: string,
  before: unknown,
  after: unknown,
): DescribedChange {
  const list = listChange(before, after);
  if (list.kind === 'added' || list.kind === 'removed')
    return addedOrRemovedColumn(
      list,
      sheet,
      { added: 'column-added', removed: 'column-removed' },
      'name',
    );
  if (list.kind === 'reordered')
    return { change: { kind: 'columns-reordered', sheet } };
  if (list.kind !== 'changed')
    return { change: { kind: 'edited', item: 'sheet', name: sheet } };
  const column = text(list.element.name);
  const group = textGroup(id, `columns:${list.index}`, list.field);
  if (list.field === 'name')
    return {
      change: {
        kind: 'column-renamed',
        sheet,
        from: text(list.before.name),
        to: column,
      },
      ...group,
    };
  const kind = isColumnField(list.field)
    ? COLUMN_FIELD_CHANGES[list.field]
    : 'column-edited';
  return { change: { kind, sheet, column }, ...group };
}

function describeSort(sheet: string, after: unknown): DescribedChange {
  const [first] = records(after);
  if (!first) return { change: { kind: 'sort-cleared', sheet } };
  return {
    change: {
      kind: 'sorted',
      sheet,
      column: text(first.column),
      direction: first.direction === 'desc' ? 'desc' : 'asc',
    },
  };
}

function describeLookups(
  id: string,
  sheet: string,
  before: unknown,
  after: unknown,
): DescribedChange {
  const list = listChange(before, after);
  if (list.kind === 'added' || list.kind === 'removed')
    return addedOrRemovedColumn(
      list,
      sheet,
      { added: 'lookup-added', removed: 'lookup-removed' },
      'outputColumn',
    );
  if (list.kind !== 'changed')
    return { change: { kind: 'edited', item: 'sheet', name: sheet } };
  return {
    change: {
      kind: 'lookup-changed',
      sheet,
      column: text(list.element.outputColumn),
    },
    ...textGroup(id, `lookups:${list.index}`, list.field),
  };
}

function cellsOf(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};
}

/** A freeform sheet's cells edit: one cell by its address, else several. */
function describeCells(
  sheet: string,
  before: unknown,
  after: unknown,
): DescribedChange {
  const old = cellsOf(before);
  const next = cellsOf(after);
  const changed = [
    ...new Set([...Object.keys(old), ...Object.keys(next)]),
  ].filter((address) => old[address] !== next[address]);
  const [cell] = changed;
  if (changed.length !== 1 || cell === undefined)
    return { change: { kind: 'cells-edited', sheet } };
  return {
    change: {
      kind: cell in next ? 'cell-edited' : 'cell-cleared',
      sheet,
      cell,
    },
  };
}

/**
 * A Worksheet's edit by the part it touched. A column rename carries its
 * filters and sorts along and a deleted column takes its own, so a change to
 * the columns is named for the columns whatever else follows.
 */
function describeSheetPart(
  old: Entry,
  entry: Entry,
  keys: readonly string[],
): DescribedChange | null {
  const sheet = text(entry.name);
  const only = keys.length === 1 ? keys[0] : undefined;
  if (keys.includes('columns'))
    return describeColumns(entry.id, sheet, old.columns, entry.columns);
  if (only === 'filters')
    return describeFilters(entry.id, sheet, old.filters, entry.filters);
  if (only === 'sort') return describeSort(sheet, entry.sort);
  if (only === 'lookups')
    return describeLookups(entry.id, sheet, old.lookups, entry.lookups);
  if (only === 'pivot') return { change: { kind: 'pivot-changed', sheet } };
  if (only === 'cells') return describeCells(sheet, old.cells, entry.cells);
  if (only === 'table') return { change: { kind: 'table-changed', sheet } };
  return null;
}

/** An edit named only by the item it touched: a rename, a move, else an edit. */
function describeItemEdit(
  collection: Collection,
  old: Entry,
  entry: Entry,
  keys: readonly string[],
): DescribedChange {
  const item = COLLECTIONS[collection];
  const name = entryName(collection, entry);
  const [key, ...more] = keys;
  if (key === 'name' && !more.length)
    return {
      change: {
        kind: 'renamed',
        item,
        from: text(old.name),
        to: text(entry.name),
      },
      group: `${entry.id}:name`,
    };
  if (key === 'parentId' && !more.length)
    return { change: { kind: 'moved', item, name } };
  const group =
    key !== undefined && !more.length && TEXT_FIELDS.has(key)
      ? { group: `${entry.id}:${key}` }
      : {};
  return { change: { kind: 'edited', item, name }, ...group };
}

/** One item changed in place, or null when only its review marker did. */
function describeEntryChange(
  collection: Collection,
  old: Entry,
  entry: Entry,
): DescribedChange | null {
  // An edit marks the Instructions linked to what it changed for review;
  // the marker is a consequence of the edit, not an edit of its own.
  const keys = changedKeys(old, entry).filter(
    (key) => !(collection === 'processSteps' && key === 'reviewStatus'),
  );
  if (!keys.length) return null;
  const part =
    collection === 'sheets'
      ? describeSheetPart(old, entry, keys)
      : collection === 'sources' && keys.length === 1 && keys[0] === 'filters'
        ? describeFilters(
            entry.id,
            text(entry.name),
            old.filters,
            entry.filters,
          )
        : null;
  return part ?? describeItemEdit(collection, old, entry, keys);
}

/** The entries of `entries` whose id `other` lacks, as `kind` changes. */
function missingFrom(
  collection: Collection,
  entries: readonly Entry[],
  other: ReadonlyMap<string, Entry>,
  kind: 'added' | 'removed',
): DescribedChange[] {
  return entries
    .filter((entry) => !other.has(entry.id))
    .map((entry) => ({
      change: {
        kind,
        item: COLLECTIONS[collection],
        name: entryName(collection, entry),
      },
    }));
}

/** The ids of `entries` that `other` also holds, in order. */
function sharedOrder(
  entries: readonly Entry[],
  other: ReadonlyMap<string, Entry>,
): string[] {
  return entries
    .filter((entry) => other.has(entry.id))
    .map((entry) => entry.id);
}

function collectionChanges(
  collection: Collection,
  before: readonly Entry[],
  after: readonly Entry[],
): DescribedChange[] {
  const previous = new Map(before.map((entry) => [entry.id, entry]));
  const next = new Map(after.map((entry) => [entry.id, entry]));
  const changes = [
    ...missingFrom(collection, after, previous, 'added'),
    ...missingFrom(collection, before, next, 'removed'),
    ...after.flatMap((entry) => {
      const old = previous.get(entry.id);
      const described =
        old && old !== entry
          ? describeEntryChange(collection, old, entry)
          : null;
      return described ? [described] : [];
    }),
  ];
  if (changes.length) return changes;
  return same(sharedOrder(before, next), sharedOrder(after, previous))
    ? []
    : [{ change: { kind: 'reordered' } }];
}

/**
 * Names what changed from `before` to `after` for the edit history: the one
 * thing it did when it did one, else an edit of the whole Workbook.
 */
export function describeDefinitionChange(
  before: WorkbookDefinition,
  after: WorkbookDefinition,
): DescribedChange {
  const changes: DescribedChange[] = [];
  if (before.name !== after.name)
    changes.push({
      change: { kind: 'renamed-workbook' },
      group: 'workbook:name',
    });
  if (before.instructions !== after.instructions)
    changes.push({
      change: { kind: 'edited-instructions' },
      group: 'workbook:instructions',
    });
  for (const collection of Object.keys(COLLECTIONS) as Collection[])
    if (before[collection] !== after[collection])
      changes.push(
        ...collectionChanges(collection, before[collection], after[collection]),
      );
  const [only, ...more] = changes;
  return only && !more.length ? only : { change: { kind: 'edited-workbook' } };
}
